<template>
  <cs-form-dialog
    :id="`publish-draft-dialog-${moduleId}`"
    v-model="popupDialog"
    max-width="750px"
    heading="Publish Content"
    :primary-action="{
      label: 'Update',
      eventName: 'dlg-ok',
      disabled: loading
    }"
    :secondary-action1="{ label: 'Cancel', eventName: 'dlg-cancel', disabled: loading }"
    @primary-click="handlePrimaryClick"
    @secondary1-click="cancelDialog"
  >
    <template #cs-form-dialog-content>
      <v-form>
        <v-container fluid="fluid">
          <v-row>
            <v-col cols="12">
              <c-e-content-editor-tip-tap
                id="publish-cgc-dialog--release-notes-editor"
                v-model="editorTextContent"
                :show-dialog="popupDialog"
                :has-toolbar="true"
              ></c-e-content-editor-tip-tap>
              <div v-if="requiredError" class="text-error">A summary is required</div>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
  </cs-form-dialog>
  <group-publish-dialog v-model="groupPublishDialog" @publish="handleGroupPublish" />
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import TurndownService from 'turndown';
import { CSBase, CSFormDialog } from '@complispace/cs-design-system';
import CEContentEditorTipTap from '@/components/content/CEContentEditorTipTap';
import { publish, client } from '@/api';
import {
  KEEP_MARKDOWN_TAGS,
  MESSAGES,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING,
  PUBLISH_OPTIONS
} from '@/constants';
import * as types from '@/store/mutationTypes';
import dataManager from '@/lib/engine';
import { plugInAdapter } from '@/lib/globalPlugIns';
import GroupPublishDialog from '@/components/content/GroupPublishDialog';

const GROUP_PUBLISH_FALLBACK_MESSAGE =
  'The update was successfully applied to the parent site only. Please publish the changes to the child sites via the <href>Group Publish</href> page.';

export default {
  name: 'PublishCGCDialog',

  components: {
    CEContentEditorTipTap,
    CSFormDialog,
    GroupPublishDialog
  },

  extends: CSBase,

  props: {
    showDialog: {
      type: Boolean,
      required: true,
      default: false
    },

    moduleId: {
      type: Number
    },

    moduleChildActivated: {
      type: Boolean,
      default: false
    },

    moduleToc: {
      type: Object,
      required: false
    },

    isDraftModeEnabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['closed', 'published'],

  data() {
    return {
      popupDialog: false,
      editorTextContent: '',
      requiredError: false,
      groupPublishDialog: false
    };
  },

  computed: {
    ...mapState({
      subdomain: (state) => state.session.subdomain,
      client: (state) => state.client
    }),

    ...mapGetters({
      activatedCEModules: 'activatedCEModules/items',
      isClientView: 'authorisation/isClientView',
      appName: 'app/name'
    }),

    isGroupParent() {
      if (this.client.children && this.client.children.length > 0) {
        return true;
      }
      return false;
    }
  },

  watch: {
    showDialog(value) {
      if (value) {
        this.popupDialog = true;
      }
    },

    popupDialog(value) {
      if (!value) {
        this.editorTextContent = '';
        this.requiredError = false;
        this.$emit('closed');
      }
    },

    editorTextContent() {
      this.requiredError = false;
    }
  },

  methods: {
    ...mapMutations({
      setDataUpdatedModulesState: types.DATA_SET_UNPUBLISHED_UPDATES_STATE
    }),

    getActivatedCEModule(moduleId) {
      if (this.activatedCEModules && this.activatedCEModules.length) {
        return this.activatedCEModules.find((module) => module.id === moduleId);
      }
      return null;
    },

    cancelDialog() {
      this.popupDialog = false;
    },

    async publishDraft(publishOption) {
      try {
        const turndownService = new TurndownService();
        // Keep below HTML tags in markdown.
        turndownService.keep(KEEP_MARKDOWN_TAGS);
        const mdContent = turndownService.turndown(this.editorTextContent);

        if (mdContent.trim()) {
          this.setLoading(true);

          if (this.moduleToc) {
            // For page reordering, pass updated TOC to backend
            await publish.publishDraft(this.moduleId, mdContent, this.moduleToc);
          } else {
            const response = await publish.publishDraft(
              this.moduleId,
              mdContent,
              undefined,
              true,
              this.isDraftModeEnabled
            );
            if (response.status === 201 && response.data && response.data.eventTrackingData) {
              this.trackUGCPublishEvent(response.data.eventTrackingData);
            }
          }

          this.setDataUpdatedModulesState(dataManager.MANAGER_STATE.STATE_IDLE);
          this.$emit('published', this.moduleId);

          if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_ONLY) {
            this.popupDialog = false;
          }
          this.setLoading(false);
          return { success: true };
        } else {
          this.requiredError = true;
        }
      } catch (err) {
        if (
          err.response &&
          err.response.data &&
          err.response.data.message === 'OperationNotAllowed'
        ) {
          // If page reordering can't be done as there have been updates made to the master branch TOC,
          // throw an error to refresh the page before reordering again.
          this.showWarningAlert(MESSAGES.ERROR.REORDERING_PUBLISH_ERROR, true);
        } else {
          this.showErrorAlert(err.message, true);
        }

        this.setLoading(false);
        return { success: false };
      }
    },

    trackUGCPublishEvent(eventTrackingData) {
      try {
        const { plugIns } = this.$app;

        const userRole = this.isClientView
          ? MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.CLIENT
          : MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN;
        const currentModule = this.getActivatedCEModule(this.moduleId);
        let moduleOwnerId;
        let moduleName;
        if (currentModule) {
          moduleOwnerId = currentModule.clientId;
          moduleName = currentModule.displayName;
        }

        const commonMixpanelProperties = {
          appName: this.appName,
          client: this.client,
          subdomain: this.subdomain,
          userRole,
          moduleId: this.moduleId,
          moduleOwnerId,
          moduleName
        };

        if (eventTrackingData.isFirstPublish) {
          // Track UGC Module Creation event in Mixpanel.
          const properties =
            plugInAdapter.Adapter.getCustomMixpanelProperties(commonMixpanelProperties);

          plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_MODULE_CREATION, properties);
        }

        if (eventTrackingData.pageDifferences) {
          // Track UGC Page Creation, UGC Page Updation and UGC Page Deletion events in Mixpanel.
          const { added, modified, deleted } = eventTrackingData.pageDifferences;

          if (added && added.length) {
            added.forEach((addedPage) => {
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                pagePath: addedPage.fileName,
                pageTitle: addedPage.pageName
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_PAGE_CREATION, properties);
            });
          }

          if (modified && modified.length) {
            modified.forEach((modifiedPage) => {
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                pagePath: modifiedPage.fileName,
                pageTitle: modifiedPage.pageName
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_PAGE_UPDATE, properties);
            });
          }

          if (deleted && deleted.length) {
            deleted.forEach((deletedPage) => {
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                pagePath: deletedPage.fileName,
                pageTitle: deletedPage.pageName
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_PAGE_DELETION, properties);
            });
          }
        }
      } catch (error) {
        console.log('Mixpanel Error: ', error);
      }
    },

    async handlePrimaryClick() {
      if (this.isGroupParent) {
        this.groupPublishDialog = true;
      } else {
        await this.publishDraft(PUBLISH_OPTIONS.PARENT_SITE_ONLY);
      }
    },

    async handleGroupPublish(publishOption) {
      if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_ONLY) {
        await this.publishDraft(publishOption);
      } else if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_AND_CHILD_SITES) {
        const publishResult = await this.publishDraft(publishOption);
        if (publishResult && publishResult.success) {
          await this.notifyChildSites(this.moduleId);
        }
      } else if (publishOption === PUBLISH_OPTIONS.ALL_SITES) {
        const publishResult = await this.publishDraft(publishOption);
        if (publishResult && publishResult.success) {
          await this.publishAndAcceptChildSites(this.moduleId);
        }
      }
    },

    async notifyChildSites(moduleId) {
      try {
        this.setLoading(true);
        const response = await client.notify(false, [moduleId]);
        if (response.status === 201) {
          const message = 'The Group Publish for the selected module was successful.';
          this.showSuccessAlert(message);
        } else if (response.status === 202) {
          const message = 'Please apply module updates before notifying your child clients.';
          this.showWarningAlert(message);
        } else if (response.status === 203) {
          const message = 'There were no module update notifications to send.';
          this.showWarningAlert(message);
        }
      } catch (error) {
        this.showWarningAlert(GROUP_PUBLISH_FALLBACK_MESSAGE, false, {
          href: '/preferences/groupAdmin/group-publish'
        });
      } finally {
        this.popupDialog = false;
        this.setLoading(false);
      }
    },
    async publishAndAcceptChildSites(moduleId) {
      try {
        this.setLoading(true);
        const response = await client.publishUpdateChildren([moduleId]);
        if (response.status === 200 && response.data[0].status !== 'ERROR') {
          const message =
            'Your update was successful and you may continue with other tasks in the meantime. To monitor the progress, check the <href>Processing Queue</href>.';
          this.showSuccessAlert(message, false, {
            href: '/preferences/groupAdmin/processing-queue'
          });
        } else {
          this.showWarningAlert(GROUP_PUBLISH_FALLBACK_MESSAGE, false, {
            href: '/preferences/groupAdmin/group-publish'
          });
        }
      } catch (error) {
        this.showWarningAlert(GROUP_PUBLISH_FALLBACK_MESSAGE, false, {
          href: '/preferences/groupAdmin/group-publish'
        });
      } finally {
        this.popupDialog = false;
        this.setLoading(false);
      }
    }
  }
};
</script>
